import { useNavigate } from "react-router";
import type { UserRole } from "../types";

interface TeamPlanRestrictionCalloutProps {
	userRole?: UserRole | null;
	workspaceId?: string | null;
}

export function getTeamPlanRestrictionMessage(userRole?: UserRole | null) {
	if (userRole === "admin") {
		return "Your current plan does not support team members. Click to upgrade.";
	}

	return "Your current plan does not support team members. Ask a workspace admin to upgrade.";
}

export function TeamPlanRestrictionBolt({ userRole, workspaceId }: TeamPlanRestrictionCalloutProps) {
	const navigate = useNavigate();
	const tooltipMessage = getTeamPlanRestrictionMessage(userRole);
	const icon = (
		<svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
		</svg>
	);

	if (userRole === "admin" && workspaceId) {
		return (
			<button
				type="button"
				onClick={() => navigate(`/${workspaceId}/billing`)}
				className="p-1.5 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
				aria-label={tooltipMessage}
			>
				{icon}
			</button>
		);
	}

	return (
		<span className="p-1.5 rounded-md" aria-label={tooltipMessage}>
			{icon}
		</span>
	);
}

export function TeamPlanRestrictionCallout({ userRole, workspaceId }: TeamPlanRestrictionCalloutProps) {
	const navigate = useNavigate();
	const isAdmin = userRole === "admin" && Boolean(workspaceId);

	return (
		<div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3 min-w-0 flex-1">
					<svg className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
						<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
					</svg>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-blue-900">Your current plan does not support team members.</p>
						<p className="mt-1 text-sm text-blue-800">Upgrade to invite collaborators and activate the currently restricted editor users in this workspace.</p>
						{!isAdmin && <p className="mt-3 text-xs font-medium text-blue-800">Ask a workspace admin to upgrade the plan to enable team invites.</p>}
					</div>
				</div>
				{isAdmin && (
					<button
						type="button"
						onClick={() => navigate(`/${workspaceId}/billing`)}
						className="shrink-0 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
					>
						Upgrade
					</button>
				)}
			</div>
		</div>
	);
}
